import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

const ASC = 'asc';
const DESC = 'desc';
const SORT_ORDERS = [ASC, DESC, null];

export interface SortableTableColumn {
  header: JSX.Element | string;
  accessorKey: string | null;
  getSortValue: (row: SortableTableRow) => string | number;
  transform: (value: unknown) => string | JSX.Element;
  sortable: boolean;
  render: (
    column: SortableTableColumn,
    row: SortableTableRow,
    props: SortableTableProps
  ) => string | JSX.Element;
}

type SortableTableColumnOverrides = Partial<SortableTableColumn>;

export type SortableTableRow = object;

interface RowWithIndexSignature {
  [key: string]: unknown;
}

interface EmptyTableRowProps {
  columnNumber: number;
  emptyTableText: string;
}

function EmptyTableRow(props: EmptyTableRowProps): JSX.Element {
  return (
    <tr>
      <td colSpan={props.columnNumber} className="u-faded">
        {props.emptyTableText}
      </td>
    </tr>
  );
}

EmptyTableRow.propTypes = {
  columnNumber: PropTypes.number.isRequired,
  emptyTableText: PropTypes.string.isRequired,
};

interface State {
  sortColumnIdx: number | null;
  sortOrder: string | null;
  activeRow: SortableTableRow | null;
}

interface SortableTableProps {
  columns: Array<SortableTableColumn>;
  rows: Array<SortableTableRow>;
  onClickRow?: (row: SortableTableRow, e?: ElementMouseEvent) => void;
  emptyTableText?: string;
  clickable?: boolean;
  selectable?: boolean;
  hoverable?: boolean;
  coloredHeader?: boolean;
  verticalAlignMiddle?: boolean;
  className?: string;
  wrapCell?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * (BNCH-13149) SortableTable is outdated, but we don't have a suitable replacement for it so cannot mark
 * it as legacy yet. We should either write a replacement, or update the implementation and behavior.
 */
class SortableTable extends React.Component<SortableTableProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        header: PropTypes.node.isRequired,
        accessorKey: PropTypes.string,
        transform: PropTypes.func,
        sortable: PropTypes.bool.isRequired,
        render: PropTypes.func.isRequired,
      })
    ).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClickRow: PropTypes.func,
    emptyTableText: PropTypes.string.isRequired,

    // Clickable shows mouse pointer (implies there is an onClick handler)
    clickable: PropTypes.bool.isRequired,

    // Selectable means that the row will show as selected when it is clicked.
    selectable: PropTypes.bool.isRequired,
    hoverable: PropTypes.bool.isRequired,
    coloredHeader: PropTypes.bool.isRequired,
    verticalAlignMiddle: PropTypes.bool.isRequired,
    className: PropTypes.string,
    wrapCell: PropTypes.bool.isRequired,
    dataTestAttrs: DataTestAttrsType,
  };

  static defaultColumn = (
    columnName: JSX.Element | string,
    accessorKey: string,
    overrides?: SortableTableColumnOverrides
  ): SortableTableColumn => {
    if (overrides == null) {
      overrides = {};
    }
    const defaults: SortableTableColumn = {
      header: columnName,
      accessorKey,
      // Note: null values do not sort so use this to override
      getSortValue(row: SortableTableRow): string | number {
        return (row as RowWithIndexSignature)[accessorKey] as string | number;
      },
      transform: (val: string | JSX.Element) => val,
      sortable: true,
      render(column: SortableTableColumn, row: SortableTableRow): string | JSX.Element {
        return column.transform((row as RowWithIndexSignature)[accessorKey]);
      },
    };
    return reactUpdate(defaults, {$merge: overrides});
  };

  static defaultProps = {
    clickable: false,
    selectable: false,
    hoverable: true,
    coloredHeader: false,
    verticalAlignMiddle: false,
    wrapCell: false,
    emptyTableText: 'No rows',
  };

  override state: State = {
    sortColumnIdx: null,
    sortOrder: null,
    activeRow: null,
  };

  getSortedRows(): Array<SortableTableRow> {
    let rows;
    if (this.state.sortColumnIdx != null && this.state.sortOrder != null) {
      rows = _.sortBy(this.props.rows, this.props.columns[this.state.sortColumnIdx].getSortValue);
      if (this.state.sortOrder === DESC) {
        rows.reverse();
      }
    } else {
      ({rows} = this.props);
    }
    return rows;
  }

  rowClicked(row: SortableTableRow, e?: ElementMouseEvent): void {
    this.setState({activeRow: row});
    if (typeof this.props.onClickRow === 'function') {
      this.props.onClickRow(row, e);
    }
  }

  sortColumn(columnIdx: number | null): void {
    let nextSortOrder;
    if (this.state.sortColumnIdx === columnIdx) {
      nextSortOrder = SORT_ORDERS[SORT_ORDERS.indexOf(this.state.sortOrder) + 1];
    } else {
      nextSortOrder = ASC;
    }

    if (nextSortOrder == null) {
      columnIdx = null;
    }
    this.setState({sortColumnIdx: columnIdx, sortOrder: nextSortOrder});
  }

  renderColumnHeader(column: SortableTableColumn, columnIdx: number): JSX.Element {
    return (
      <th key={columnIdx} className="u-no-select" scope="col">
        {column.sortable ? (
          <a
            className="u-no-text-decoration u-vertical-center-items"
            onClick={() => {
              this.sortColumn(columnIdx);
            }}
          >
            {column.header}
            {this.state.sortColumnIdx === columnIdx && (
              <div className="u-mini-spacing-left">
                <BenchlingIcon icon={this.state.sortOrder === ASC ? 'arrow-up' : 'arrow-down'} />
              </div>
            )}
          </a>
        ) : (
          column.header
        )}
      </th>
    );
  }

  renderRow(row: SortableTableRow, rowIdx: number): JSX.Element {
    return (
      <tr
        key={rowIdx}
        onClick={() => {
          this.rowClicked(row);
        }}
        className={classNames('sortableTable-row', {
          warning: this.state.activeRow === row && this.props.selectable,
          'u-pointer': this.props.clickable,
        })}
      >
        {this.props.columns.map((column, idx) => (
          <td key={idx} className={classNames({'sortableTable-cell-wrap': this.props.wrapCell})}>
            {column.render(column, row, this.props)}
          </td>
        ))}
      </tr>
    );
  }

  override render(): JSX.Element {
    const rows = this.getSortedRows();

    return (
      <table
        className={classNames(this.props.className, 'table', {
          'table-hover': this.props.hoverable,
          'table-inlineHeader': this.props.coloredHeader,
          'sortableTable-verticalAlignMiddle': this.props.verticalAlignMiddle,
        })}
        {...getTestProps(this.props.dataTestAttrs)}
      >
        <thead>
          <tr className={classNames({active: this.props.coloredHeader})}>
            {this.props.columns.map((column, colIdx) => this.renderColumnHeader(column, colIdx))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyTableRow
              columnNumber={this.props.columns.length}
              emptyTableText={this.props.emptyTableText!}
            />
          ) : (
            rows.map((row, rowIdx) => this.renderRow(row, rowIdx))
          )}
        </tbody>
      </table>
    );
  }
}

export default SortableTable;
